import React from 'react';
import { Phone, Clock, MessageCircle, MapPin, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { branches } from '../mock';

const Home = () => {
  const openWhatsApp = (phone, branchName) => {
    const message = encodeURIComponent(`Hello, I would like to inquire about diagnostic services at ${branchName}.`);
    window.open(`https://wa.me/91${phone}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between relative overflow-hidden font-sans">
      {/* Background soft glow effects */}
      <div className="absolute top-0 left-0 right-0 h-[600px] bg-gradient-to-b from-emerald-50/40 via-blue-50/20 to-transparent -z-10 pointer-events-none" />
      <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-emerald-100/30 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute -top-[10%] -right-[10%] w-[50%] h-[50%] bg-blue-100/30 rounded-full blur-[120px] -z-10 pointer-events-none" />

      {/* Main Content Area */}
      <main className="flex-grow flex flex-col items-center justify-center py-12 px-4 max-w-6xl mx-auto w-full">
        
        {/* Logo Section */}
        <div className="flex flex-col items-center justify-center mb-10 text-center animate-fade-in">
          <div className="relative group">
            {/* Glowing outer ring */}
            <div className="absolute -inset-2 bg-gradient-to-r from-emerald-500 to-blue-700 rounded-3xl blur-lg opacity-20 group-hover:opacity-35 transition duration-1000 group-hover:duration-200"></div>
            
            {/* Image frame */}
            <div className="relative bg-white p-3 rounded-2xl shadow-xl border border-slate-100/80 transition duration-300 hover:scale-[1.02] flex items-center justify-center">
              <img
                src={`${process.env.PUBLIC_URL}/logo.jpg`}
                alt="S S Diagnostic Centre Logo"
                className="h-24 md:h-28 object-contain"
              />
            </div>
          </div>
          <span className="mt-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100/50 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            Delivering Happiness...
          </span>
        </div>

        {/* Operational Timings Card */}
        <div className="w-full max-w-2xl mb-12">
          <Card className="border-slate-100 shadow-xl shadow-slate-100/40 bg-white/95 backdrop-blur-md overflow-hidden relative transition-all duration-300 hover:shadow-2xl hover:shadow-slate-100/60">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-blue-700" />
            
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-emerald-50/80 border border-emerald-100 flex items-center justify-center mb-3">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-800 tracking-tight">Operational Timings</CardTitle>
              <CardDescription className="text-slate-500">Serving you seven days a week with clinical excellence</CardDescription>
            </CardHeader>
            
            <CardContent className="py-6 px-4 md:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                <div className="flex flex-col items-center justify-center text-center pb-5 md:pb-0">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Monday – Saturday</span>
                  <span className="text-2xl font-extrabold text-slate-800 tracking-tight">10:00 AM – 09:00 PM</span>
                  <span className="text-xs text-emerald-600 font-medium mt-1.5">Full Day Diagnostic Services</span>
                </div>
                <div className="flex flex-col items-center justify-center text-center pt-5 md:pt-0">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Sunday</span>
                  <span className="text-2xl font-extrabold text-slate-800 tracking-tight">10:00 AM – 03:00 PM</span>
                  <span className="text-xs text-blue-600 font-medium mt-1.5">Half Day Schedule</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Branches & Contact Info */}
        <div className="w-full">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight mb-2">Our Locations & Mobile Numbers</h2>
            <p className="text-slate-500 text-sm max-w-md mx-auto">Get in touch directly with our branches for appointments and reports</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {branches.map((branch) => (
              <Card key={branch.id} className="border-slate-100 shadow-lg shadow-slate-100/30 bg-white/95 backdrop-blur-sm flex flex-col justify-between overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-bold text-slate-800">{branch.name}</CardTitle>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 flex-grow flex flex-col justify-between">
                  {/* Address Section */}
                  <div className="flex items-start gap-2.5 text-slate-500">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs leading-relaxed text-slate-600">{branch.address}</p>
                  </div>

                  {/* Phone List Section */}
                  <div className="space-y-2.5 pt-2 border-t border-slate-50">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Mobile Numbers</div>
                    
                    {branch.phones.map((phone, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50/70 border border-slate-100 hover:border-emerald-100 hover:bg-emerald-50/20 transition-all duration-200 group/item">
                        <div className="flex items-center gap-2">
                          <Phone className="w-3.5 h-3.5 text-emerald-600" />
                          <a href={`tel:${phone}`} className="font-semibold text-sm text-slate-700 tracking-wide hover:text-emerald-700 hover:underline">
                            {phone}
                          </a>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.location.href = `tel:${phone}`}
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-100/50 rounded-lg transition-colors"
                            title="Call Now"
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openWhatsApp(phone, branch.name)}
                            className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100/50 rounded-lg transition-colors"
                            title="Send WhatsApp Message"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200/60 bg-white py-6 px-4 mt-8 relative z-10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left text-xs text-slate-500">
          <div>
            <p className="font-semibold text-slate-700">© 2026 S S Diagnostic Centre™</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Delivering Happiness through Accurate Diagnostics</p>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-slate-400">
            <span>Powered by</span>
            <span className="font-semibold text-slate-600">ICS PVT. LTD.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;

